'use client';

import Icon from '@/components/ui/AppIcon';

type ServiceKind = 'sofa' | 'curtain' | 'carpet' | 'upholstery' | 'bed';

const NOTES_BY_KIND: Record<ServiceKind, string[]> = {
  sofa: [
    'Nominal transportation charge will be charged (depending on your location).',
    'In a fully upholstered sofa, if loose cushions or gaddi/backrests are kept, they will be charged extra as mentioned above.',
    'The above prices are for a normal sofa set. If sofas are found bigger, the prices will vary accordingly.',
    'The above prices are subject to change without any prior notice.',
  ],
  curtain: [
    'Nominal transportation charge will be charged (depending on your location).',
    'Prices are for standard fabric curtains. Heavy, blackout, or specialty fabrics may be charged differently.',
    'Final area is measured by our technician on-site to confirm the price.',
    'The above prices are subject to change without any prior notice.',
  ],
  carpet: [
    'Nominal transportation charge will be charged (depending on your location).',
    'Prices are for standard pile carpets. Wool, silk, or premium handmade carpets may be charged differently.',
    'Final area is measured by our technician on-site to confirm the price.',
    'The above prices are subject to change without any prior notice.',
  ],
  upholstery: [
    'Nominal transportation charge will be charged (depending on your location).',
    'Prices are for standard upholstery. Premium fabrics or oversized pieces may be charged differently.',
    'The above prices are subject to change without any prior notice.',
  ],
  bed: [
    'Nominal transportation charge will be charged (depending on your location).',
    'In a fully upholstered bed, if loose cushions or pillows are kept on it, they will be charged extra as mentioned above.',
    'The above prices are for a normal bed. If beds are found bigger, the prices will vary accordingly.',
    'The above prices are subject to change without any prior notice.',
  ],
};

interface ServicePricingNotesProps {
  /** Drives which set of disclaimers to render. */
  kind: ServiceKind;
  /** Optional override — pass an explicit list of notes if the service has
   *  bespoke copy that doesn't fit the default per-kind set. */
  notes?: string[];
  className?: string;
}

/**
 * Pricing disclaimers panel ("Please note: …").
 *
 * Previously rendered on the service-selection card BEFORE the customer
 * picks measurements. Per the chat that introduced this component, the
 * panel now lives at the bottom of the measurement step so customers see
 * the disclaimers AFTER they've entered their carpets/curtains/seats —
 * the time when they care about caveats like "wool charged differently"
 * or "final area confirmed on-site".
 *
 * Same visual treatment across every service kind so the booking flow has
 * a consistent rhythm; copy is parameterised so each kind keeps its own
 * caveats (sofa cares about loose cushions, carpet about premium pile, etc).
 */
const ServicePricingNotes = ({
  kind,
  notes,
  className = '',
}: ServicePricingNotesProps) => {
  const items = notes && notes.length > 0 ? notes : NOTES_BY_KIND[kind];
  if (!items || items.length === 0) return null;

  return (
    <div data-component-id="src/app/service-booking-flow/components/ServicePricingNotes.tsx:71:4" data-component-path="src/app/service-booking-flow/components/ServicePricingNotes.tsx" data-component-line="71" data-component-end-line="87" data-component-file="ServicePricingNotes.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`bg-[#F8FAFB] border border-[#E4EDF0] rounded-2xl p-5 ${className}`}
      data-testid="service-pricing-notes"
    >
      <div data-component-id="src/app/service-booking-flow/components/ServicePricingNotes.tsx:75:6" data-component-path="src/app/service-booking-flow/components/ServicePricingNotes.tsx" data-component-line="75" data-component-end-line="78" data-component-file="ServicePricingNotes.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20mb-3%22%7D" className="flex items-center gap-2 mb-3">
        <Icon data-component-id="src/app/service-booking-flow/components/ServicePricingNotes.tsx:76:8" data-component-path="src/app/service-booking-flow/components/ServicePricingNotes.tsx" data-component-line="76" data-component-end-line="76" data-component-file="ServicePricingNotes.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22InformationCircleIcon%22%2C%22className%22%3A%22text-%5B%23204653%5D%22%7D" name="InformationCircleIcon" size={18} className="text-[#204653]" />
        <h4 data-component-id="src/app/service-booking-flow/components/ServicePricingNotes.tsx:77:8" data-component-path="src/app/service-booking-flow/components/ServicePricingNotes.tsx" data-component-line="77" data-component-end-line="77" data-component-file="ServicePricingNotes.tsx" data-component-name="h4" data-component-content="%7B%22elementName%22%3A%22h4%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22Please%20note%22%7D" className="text-sm font-semibold text-[#1D1D1F]">Please note</h4>
      </div>
      <ul data-component-id="src/app/service-booking-flow/components/ServicePricingNotes.tsx:79:6" data-component-path="src/app/service-booking-flow/components/ServicePricingNotes.tsx" data-component-line="79" data-component-end-line="86" data-component-file="ServicePricingNotes.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22space-y-2.5%20text-sm%20text-%5B%234b5563%5D%20leading-relaxed%22%7D" className="space-y-2.5 text-sm text-[#4b5563] leading-relaxed">
        {items.map((note, idx) => (
          <li data-component-id="src/app/service-booking-flow/components/ServicePricingNotes.tsx:81:10" data-component-path="src/app/service-booking-flow/components/ServicePricingNotes.tsx" data-component-line="81" data-component-end-line="84" data-component-file="ServicePricingNotes.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22flex%20gap-2%22%7D" key={idx} className="flex gap-2">
            <span data-component-id="src/app/service-booking-flow/components/ServicePricingNotes.tsx:82:12" data-component-path="src/app/service-booking-flow/components/ServicePricingNotes.tsx" data-component-line="82" data-component-end-line="82" data-component-file="ServicePricingNotes.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22mt-2%20w-1.5%20h-1.5%20rounded-full%20bg-%5B%23204653%5D%20flex-shrink-0%22%7D" className="mt-2 w-1.5 h-1.5 rounded-full bg-[#204653] flex-shrink-0"></span>
            <span data-component-id="src/app/service-booking-flow/components/ServicePricingNotes.tsx:83:12" data-component-path="src/app/service-booking-flow/components/ServicePricingNotes.tsx" data-component-line="83" data-component-end-line="83" data-component-file="ServicePricingNotes.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{note}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ServicePricingNotes;
